import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FeatureFlag } from "featureFlags/constants";
import { useAllFlags } from "featureFlags/hooks/useAllFlags";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";
import { ReactQueryKeysAccount } from "services/queryKeys";

import RefreshIcon from "assets/icons/DocumentOutputRefreshIcon.svg?react";

import { useCurrentAccount } from "components/Account/useCurrentAccount";
import { useAccessManager } from "components/App/accessManager";
import { useLeftSidebarContext } from "components/LeftSidebar/context";
import { useCreateDatabase } from "components/LeftSidebar/CreateMenu/useCreateDatabase";
import { useCreateEngine } from "components/LeftSidebar/CreateMenu/useCreateEngine";
import { useCreateRole } from "components/LeftSidebar/CreateMenu/useCreateRole";
import { useCreateUser } from "components/LeftSidebar/CreateMenu/useCreateUser";
import { useChildren } from "components/LeftSidebar/DatabasesPanel/useChildren";
import { PlusIcon } from "components/Icons";
import Spinner from "components/Spinner/Spinner";

import styles from "./HeaderControls.module.scss";

type Props = {
  refreshQueryKeys?: QueryKey[];
  createType?: "database" | "engine" | "user" | "role";
};

export const HeaderControls = ({ refreshQueryKeys, createType }: Props) => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { t } = useTranslation();
  const { isAllowedTo } = useAccessManager();
  const { getAccount } = useCurrentAccount();
  const { openCreateEngine, createEngineMarkup } = useCreateEngine();
  const { openCreateDatabase, createDatabaseMarkup } = useCreateDatabase();
  const { openCreateUser, createUserMarkup } = useCreateUser();
  const { openCreateRole, createRoleMarkup } = useCreateRole();
  const { treeData, expandedItems, preserveTreeData } = useLeftSidebarContext();
  const { refreshTreeData } = useChildren();
  const flags = useAllFlags();
  const enabledGcpAccount = flags[FeatureFlag.FireboltAppEnableGcpAccount];
  const isLite = Boolean(process.env.REACT_APP_LITE);

  const handleRefresh = async () => {
    if (!refreshQueryKeys?.length || isRefreshing) {
      return;
    }
    setIsRefreshing(true);
    await Promise.all(
      refreshQueryKeys.map(queryKey =>
        queryClient.invalidateQueries({ queryKey })
      )
    );
    const shouldRefreshTree = refreshQueryKeys.some(queryKey => {
      const keyPrefix = Array.isArray(queryKey) ? queryKey[0] : queryKey;
      return (
        keyPrefix === ReactQueryKeysAccount.databasesNames ||
        keyPrefix === "database-search-tree"
      );
    });
    if (shouldRefreshTree && treeData.length > 0) {
      const refreshedTreeData = await refreshTreeData(treeData, expandedItems);
      preserveTreeData(refreshedTreeData);
    }
    setIsRefreshing(false);
  };

  const refreshDisabled = !refreshQueryKeys?.length || isRefreshing;

  const accountName = getAccount().accountName;
  const canCreateDatabase =
    isAllowedTo(
      PrivilegeObjectType.account,
      accountName,
      SqlRbacAction.DATABASE_CREATE
    ) && !enabledGcpAccount;
  const canCreateEngine =
    isAllowedTo(
      PrivilegeObjectType.account,
      accountName,
      SqlRbacAction.ENGINE_CREATE
    ) && !enabledGcpAccount;
  const canCreateRole = isAllowedTo(
    PrivilegeObjectType.account,
    accountName,
    SqlRbacAction.ROLE_CREATE
  );
  const canCreateUser = isAllowedTo(
    PrivilegeObjectType.account,
    accountName,
    SqlRbacAction.USER_CREATE
  );

  const createConfig = useMemo(() => {
    switch (createType) {
      case "database":
        return {
          enabled: canCreateDatabase,
          label: t("workspace.create_menu.database"),
          action: openCreateDatabase,
        };
      case "engine":
        return {
          enabled: canCreateEngine,
          label: t("workspace.create_menu.engine"),
          action: openCreateEngine,
        };
      case "user":
        return {
          enabled: canCreateUser,
          label: t("workspace.create_menu.user"),
          action: openCreateUser,
        };
      case "role":
        return {
          enabled: canCreateRole,
          label: t("workspace.create_menu.role"),
          action: openCreateRole,
        };
      default:
        return null;
    }
  }, [
    canCreateDatabase,
    canCreateEngine,
    canCreateRole,
    canCreateUser,
    createType,
    openCreateDatabase,
    openCreateEngine,
    openCreateRole,
    openCreateUser,
    t,
  ]);

  const showCreate = !isLite && createConfig?.enabled;

  return (
    <div className={styles.headerControls}>
      {showCreate && (
        <div
          className={styles.controlIcon}
          onClick={() => createConfig?.action?.()}
          title={createConfig?.label}
          data-testid="header-create-control"
        >
          <PlusIcon />
        </div>
      )}
      <div
        className={styles.controlIcon}
        onClick={handleRefresh}
        data-disabled={refreshDisabled}
      >
        {isRefreshing ? (
          <Spinner
            size={16}
            color="colorDisabled"
          />
        ) : (
          <RefreshIcon />
        )}
      </div>
      <Suspense fallback={null}>
        {createEngineMarkup}
        {createDatabaseMarkup}
        {createUserMarkup}
        {createRoleMarkup}
      </Suspense>
    </div>
  );
};
